import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@hooks/reduxHooks'
import { useSelector } from '@state/store'
import moment from 'moment'
import { GeneratorElutionDto, PreparationLotDto, WasteEntryDto } from '@services/dtos'
import { cancelWasteEntry, getWasteEntries, setWasteEntryFilters } from '@state/reducers/wasteEntryReducer'
import { Box, Container, IconButton, Tooltip } from '@mui/material'
import WasteEntriesTableToolbar, { WasteEntriesTableExportToolbar } from './Toolbars/WasteEntriesTableToolbar'
import { getNameWithRole } from '@utils/member.utils'
import { WasteContainerTypeChip } from '../WasteContainerChips'
import InfoIcon from '@mui/icons-material/Info'
import { PreparationLotDetailsDialog } from '@components/dialogs/PreparationLotDetailsDialog'
import { GeneratorElutionDetailsDialog } from '@components/dialogs/GeneratorElutionDetailsDialog'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import CancelIcon from '@mui/icons-material/Cancel'
import MoveDownIcon from '@mui/icons-material/MoveDown'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import { ChangeWasteEntryContainerDialog } from '@components/dialogs/ChangeWasteEntryContainerDialog'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { HotLabEquipmentTypeLabels } from '@utils/constants'

interface Props {
  containerClassName?: string
  containerId?: string
}

export default function WasteEntriesTable({ containerClassName, containerId }: Props) {
  const { wasteEntries, totalCount, loading } = useSelector(({ wasteEntry }) => ({
    wasteEntries: wasteEntry.items.datas,
    totalCount: wasteEntry.items.totalCount,
    loading: wasteEntry.loading,
  }))

  const dispatch = useAppDispatch()
  const [selectedPreparationLot, setSelectedPreparationLot] = useState<PreparationLotDto | null>(null)
  const [selectedGeneratorElution, setSelectedGeneratorElution] = useState<GeneratorElutionDto | null>(null)
  const [entryToCancel, setEntryToCancel] = useState<WasteEntryDto | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [entryToMove, setEntryToMove] = useState<WasteEntryDto | null>(null)

  useEffect(() => {
    if (containerId) {
      dispatch(setWasteEntryFilters({ containerId, page: 0 }))
    }
    dispatch(getWasteEntries())
    return () => {
      dispatch(setWasteEntryFilters({ page: 0, containerId: undefined }))
    }
  }, [])

  const renderOriginCell = (
    label: string,
    no: number,
    onClick: (e: React.MouseEvent) => void
  ) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Voir les détails">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClick(e) }}>
          <InfoIcon sx={{ fontSize: 17, color: 'grey.800' }} />
        </IconButton>
      </Tooltip>
      {label} #{no}
    </Box>
  )

  const columns: GridColumns<WasteEntryDto> = useMemo(
    () => [
      {
        field: 'actions',
        headerName: '',
        width: 80,
        disableExport: true,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex' }}>
            <Tooltip title="Changer de poubelle">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEntryToMove(params.row) }}>
                <MoveDownIcon sx={{ fontSize: 17 }} color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Annuler le déchet">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEntryToCancel(params.row) }}>
                <CancelIcon sx={{ fontSize: 17 }} color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      { field: 'no', headerName: 'N°', width: 70, type: 'number' },
      {
        field: 'createdAt',
        headerName: 'Date',
        width: 145,
        valueGetter: (params) => moment(params.row.createdAt).format('DD/MM/YYYY HH:mm'),
      },
      {
        field: 'label',
        headerName: 'Label',
        width: 125,
        valueGetter: (params) => params.row.label ?? '',
      },
      {
        field: 'wasteContainer',
        headerName: 'Conteneur à déchets',
        width: 260,
        valueGetter: (params) => params.row.wasteContainer?.label ?? '',
        renderCell: (params) => {
          const isFromTransfer = containerId && params.row.wasteContainer?.id !== containerId
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isFromTransfer && (
                <Tooltip title={'Déchet issu de transvasement'}>
                  <SwapHorizIcon fontSize="small" color={'primary'} />
                </Tooltip>
              )}
              <span>{params.row.wasteContainer?.label ?? ''}</span>
            </Box>
          )
        },
      },
      {
        field: 'origin',
        headerName: 'Source',
        width: 155,
        renderCell: (params) => {
          const { preparationLot, generatorElution } = params.row
          if (preparationLot) {
            return renderOriginCell('Préparation', preparationLot.no, () =>
              setSelectedPreparationLot(preparationLot)
            )
          }
          if (generatorElution) {
            return renderOriginCell('Élution', generatorElution.no, () =>
              setSelectedGeneratorElution(generatorElution)
            )
          }
          return 'Ajout libre'
        },
      },
      {
        field: 'wasteContainerType',
        headerName: 'Type',
        width: 85,
        renderCell: (params) => <WasteContainerTypeChip container={params.row.wasteContainer} />,
      },
      {
        field: 'isotope',
        headerName: 'Isotope',
        width: 145,
        valueGetter: (params) => params.row.isotope ? `${params.row.isotope.name ?? '' } (${params.row.isotope.code ?? ''})` : '',
      },
      {
        field: 'hotLabEquipment',
        headerName: 'Appareil de mesure',
        width: 180,
        valueGetter: (params) => {
          const equipment = params.row.hotLabEquipment
          if (!equipment) {
            return ''
          }
          return `${equipment.name} (${HotLabEquipmentTypeLabels[equipment.type]})`
        },
      },
      {
        field: 'activityAtRefMbq',
        headerName: 'MBq estimé',
        width: 130,
        type: 'number',
        renderCell: (params) => {
          const row = params.row as WasteEntryDto
          if (row.activityAtRefMbq == null) {
            return null
          }
          if (row.activityAtTimeMbq === null) {
            return <span>{row.activityAtRefMbq.toFixed(2)}</span>
          }
          return (
            <div className="flex items-center gap-1">
              <span className="font-medium">{row.activityAtTimeMbq.toFixed(2)}</span>
              <span>/ {row.activityAtRefMbq.toFixed(2)}</span>
            </div>
          )
        },
      },
      {
        field: 'activityRefAt',
        headerName: 'Date mesure activité',
        width: 145,
        valueGetter: (params) =>
          params.row.activityRefAt
            ? moment(params.row.activityRefAt).format('DD/MM/YYYY HH:mm')
            : '',
      },
      {
        field: 'massG',
        headerName: 'Masse (g)',
        width: 90,
        type: 'number',
        valueGetter: (params) => params.row.massG ? params.row.massG.toFixed(2) : '',
      },
      {
        field: 'volumeMl',
        headerName: 'Volume (mL)',
        width: 105,
        type: 'number',
        valueGetter: (params) => params.row.volumeMl ? params.row.volumeMl.toFixed(1) : '',
      },
      {
        field: 'description',
        headerName: 'Notes',
        width: 250,
        valueGetter: (params) => params.row.description ?? '',
      },
      {
        field: 'collectedBy',
        headerName: 'Collecté par',
        width: 200,
        valueGetter: (params) => params.row.collectedBy ? getNameWithRole(params.row.collectedBy) : '',
      },
      {
        field: 'createdBy',
        headerName: 'Créé par',
        width: 200,
        valueGetter: (params) => params.row.createdBy ? getNameWithRole(params.row.createdBy) : '',
      },
    ],
    [containerId],
  )

  return (
    <>
      <Container
        maxWidth={false}
        style={{ flex: 1, minHeight: 0, padding: '0px 30px', display: 'flex', flexDirection: 'column' }}
        className={containerClassName}
      >
        <DataGridPro
          density="standard"
          pagination
          pageSize={20}
          rowsPerPageOptions={[20]}
          paginationMode="server"
          components={{ 
            Toolbar: containerId ? WasteEntriesTableExportToolbar : WasteEntriesTableToolbar
          }}
          style={{
            height: '100%',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '15px 15px 25px 25px',
          }}
          onPageChange={(page) => {
            dispatch(setWasteEntryFilters({ page }))
            dispatch(getWasteEntries())
          }}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(even)': {
              backgroundColor: '#f8f8f8',
            },
          }}
          columns={columns}
          rows={wasteEntries}
          rowCount={totalCount}
          loading={loading}
          disableSelectionOnClick
          disableMultipleSelection
        />
      </Container>

      <PreparationLotDetailsDialog
        open={!!selectedPreparationLot}
        preparationLot={selectedPreparationLot}
        onClose={() => setSelectedPreparationLot(null)}
      />
      <GeneratorElutionDetailsDialog
        open={!!selectedGeneratorElution}
        generatorElution={selectedGeneratorElution}
        onClose={() => setSelectedGeneratorElution(null)}
      />

      {entryToMove && (
        <ChangeWasteEntryContainerDialog
          open={!!entryToMove}
          entry={entryToMove}
          onClose={() => setEntryToMove(null)}
        />
      )}

      <ConfirmDialog
        open={!!entryToCancel}
        title={`Annuler le déchet #${entryToCancel?.no}`}
        message={`Voulez-vous vraiment annuler le déchet "${entryToCancel?.label ?? `#${entryToCancel?.no}`}" ?`}
        confirmButtonText="Confirmer"
        severity="warning"
        isProcessing={isCancelling}
        onCancel={() => setEntryToCancel(null)}
        onConfirm={async () => {
          if (!entryToCancel) return
          setIsCancelling(true)
          dispatch(cancelWasteEntry(entryToCancel.id))
            .unwrap()
            .then(() => {
              dispatch(enqueueSnackbar({ message: 'Déchet annulé avec succès', options: { variant: 'success' } }))
              setEntryToCancel(null)
            })
            .catch(() =>
              dispatch(enqueueSnackbar({ message: 'Une erreur est survenue', options: { variant: 'error' } })),
            )
            .finally(() => setIsCancelling(false))
        }}
      />
    </>
  )
}
